// Figma: "Loyalty Cloud" file, node 18261:159418 ("Dashboard-Body" — CRM residents grid).
// Sourced from the live v2 residents API (../../../features/coreApi/residentsSlice.js's
// getResidentsByAccountSfidV2) instead of the Redux `entities.residents` the old table used
// — that endpoint returns the whole account roster in one call, this one paginates
// (offset/limit). getResidentsByAccountSfidV2's `merge` option accumulates each page into
// one growing RTK Query cache entry keyed by account sfid, so this component only needs to
// track the current `offset` and bump it on scroll — no local rows/append bookkeeping.
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {skipToken} from '@reduxjs/toolkit/query/react';
import {Box, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';

import {tokens} from '../../../themes/ThemeCustomization';
import {getDashboardAccount} from '../../../app/store/reducers/dashboard';
import {useGetResidentsByAccountSfidV2Query, useGetFilteredResidentsQuery} from '../../../features/coreApi/residentsSlice';
import ResidentDetailDrawer from './ResidentDetailDrawer';
import FilterPopover, {applyFilters, splitFilterConditions} from './FilterPopover';

import exportIcon from '../../../assets/shell/table-export-icon.svg';
import searchIcon from '../../../assets/shell/table-search-icon.svg';
import filterIcon from '../../../assets/shell/table-filter-icon.svg';
import sortIcon from '../../../assets/shell/table-sort-icon.svg';
import sortActiveIcon from '../../../assets/shell/table-sort-active-icon.svg';

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD_PX = 200;
const SEARCH_DEBOUNCE_MS = 300;

// CSV cell escaping per RFC 4180: wrap in quotes (and double up any embedded quotes)
// whenever the value contains a comma, quote, or newline, so commas inside e.g. an address
// or note don't get misread as column separators.
const toCsvCell = (value) => {
    const str = value == null ? '' : String(value);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const downloadResidentsCsv = (columns, rows) => {
    const header = columns.map((col) => toCsvCell(col.headerName));
    const lines = rows.map((row) => columns.map((col) => toCsvCell(row[`${col.field}Display`] ?? row[col.field])));
    const csv = [header, ...lines].map((line) => line.join(',')).join('\r\n');

    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `residents-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const formatCurrency = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? `$${num.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : value || '-';
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'});
};

const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? value
        : `${date.toLocaleDateString()}, ${date.toLocaleTimeString(undefined, {hour: 'numeric', minute: '2-digit'})}`;
};

const STAKE_CHECKING_STATUS_LABELS = {
    ApplicationPending: 'Application pending',
    ApplicationPendingIDVerification: 'Application verification required',
    ApplicationPendingAddressVerification: 'Application verification required',
    ApplicationPendingSSNVerification: 'Application verification required',
    ApplicationInReview: 'Application in review',
    ApplicationRejected: 'Application rejected',
    ApplicationApproved: 'Application approved',
    DepositAccountCreated: 'Deposit account created',
    DebitCardRequested: 'Debit card requested',
    DebitCardActivated: 'Debit card activated'
};

const formatStakeChecking = (value) => (value ? STAKE_CHECKING_STATUS_LABELS[value] || value : '-');

const COLUMNS = [
    {field: 'name', headerName: 'Name', width: 200},
    {field: 'community', headerName: 'Community', width: 180},
    {field: 'campaign', headerName: 'Campaign', width: 180},
    {field: 'email', headerName: 'Email', width: 220, link: true},
    {field: 'phone', headerName: 'Phone', width: 180, link: true},
    {field: 'unit', headerName: 'Unit', width: 140},
    {field: 'floorPlan', headerName: 'Floor plan', width: 180},
    {field: 'rent', headerName: 'Rent', width: 140, render: (row) => formatCurrency(row.rent)},
    {field: 'balance', headerName: 'Ledger balance', width: 160, render: (row) => formatCurrency(row.balance)},
    {field: 'leaseStart', headerName: 'Lease start date', width: 160, render: (row) => formatDate(row.leaseStart)},
    {field: 'leaseEnd', headerName: 'Lease end date', width: 160, render: (row) => formatDate(row.leaseEnd)},
    {field: 'offers', headerName: 'Current offers', width: 140}
];

const prepareRow = (res) => ({
    id: res.sfid || res.id,
    mobileUserId: res.mobile_user_id__c,
    name: res.name,
    community: res.property_name__c || res.community_name || '',
    campaign: res.renter_campaign_name__c || res.campaign_name || '',
    email: res.email,
    phone: res.phone,
    unit: res.apartment_or_unit__c,
    floorPlan: res.floor_plan_name__c,
    rent: res.rent_amount__c,
    balance: res.amount_due__c,
    leaseStart: res.lease_start_date__c,
    leaseEnd: res.lease_end_date__c,
    moveInDate: res.move_in_date__c,
    stakeChecking: res.stake_debit_status__c,
    createdAt: res.createddate || res.CreatedDate,
    notes: res.notes__c || res.notes,
    offers: res.offers?.filter((offer) => offer.reward_amount > 0).length || 0,
    rentDisplay: formatCurrency(res.rent_amount__c),
    balanceDisplay: formatCurrency(res.amount_due__c),
    leaseStartDisplay: formatDate(res.lease_start_date__c),
    leaseEndDisplay: formatDate(res.lease_end_date__c),
    moveInDateDisplay: formatDate(res.move_in_date__c),
    stakeCheckingDisplay: formatStakeChecking(res.stake_debit_status__c),
    createdAtDisplay: formatDateTime(res.createddate || res.CreatedDate)
});

const ResidentsTable = () => {
    const dashboardAccount = useSelector(getDashboardAccount);
    const accountSfid = dashboardAccount?.sfid;
    const hasAccount = Boolean(accountSfid) && accountSfid !== 'all';

    const [offset, setOffset] = useState(0);
    const [sort, setSort] = useState({field: 'name', direction: 'asc'});
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [filterConditions, setFilterConditions] = useState([]);
    const scrollRef = useRef(null);

    // Reset to the first page whenever the selected account changes.
    const lastAccountRef = useRef(accountSfid);
    if (lastAccountRef.current !== accountSfid) {
        lastAccountRef.current = accountSfid;
        if (offset !== 0) setOffset(0);
    }

    // Debounce the search box, then reset to the first page once the debounced term changes.
    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const lastSearchRef = useRef(search);
    if (lastSearchRef.current !== search) {
        lastSearchRef.current = search;
        if (offset !== 0) setOffset(0);
    }

    // Name/email/phone conditions go to the backend's /v2/residents/filter/ endpoint
    // (getFilteredResidents); everything else keeps filtering client-side below. Both row
    // sources still paginate the same way (offset/limit + infinite scroll).
    const {apiConditions, clientConditions} = useMemo(() => splitFilterConditions(filterConditions), [filterConditions]);
    const hasApiFilters = apiConditions.length > 0;

    const lastApiFiltersRef = useRef(JSON.stringify(apiConditions));
    const apiFiltersKey = JSON.stringify(apiConditions);
    if (lastApiFiltersRef.current !== apiFiltersKey) {
        lastApiFiltersRef.current = apiFiltersKey;
        if (offset !== 0) setOffset(0);
    }

    const {data: listData, isFetching: isListFetching, isLoading: isListLoading} = useGetResidentsByAccountSfidV2Query(
        hasAccount && !hasApiFilters ? {sfid: accountSfid, offset, limit: PAGE_SIZE, search} : skipToken
    );

    const {data: filteredData, isFetching: isFilterFetching, isLoading: isFilterLoading} = useGetFilteredResidentsQuery(
        hasAccount && hasApiFilters ? {sfid: accountSfid, offset, limit: PAGE_SIZE, filters: apiConditions} : skipToken
    );

    const pageData = hasApiFilters ? filteredData : listData;
    const data = pageData?.items;
    const totalCount = pageData?.total;
    const isFetching = hasApiFilters ? isFilterFetching : isListFetching;
    const isLoading = hasApiFilters ? isFilterLoading : isListLoading;

    const rows = useMemo(() => (Array.isArray(data) ? data.map(prepareRow) : []), [data]);
    // The merged cache (accumulated across all fetched pages) should have offset+PAGE_SIZE
    // entries once the page at `offset` has loaded; fewer means the last page came up short,
    // i.e. the account has no more residents to fetch.
    const hasMore = !data || (totalCount != null ? data.length < totalCount : data.length >= offset + PAGE_SIZE);

    // Client-side conditions (community, unit, rent, ...) only narrow the loaded rows, so the
    // backend total no longer applies once any are set — fall back to the visible row count.
    const hasClientFilters = clientConditions.length > 0;

    const filteredRows = useMemo(() => applyFilters(rows, clientConditions), [rows, clientConditions]);

    const sortedRows = useMemo(() => {
        if (!sort) return filteredRows;
        const {field, direction} = sort;
        const copy = [...filteredRows];
        copy.sort((a, b) => {
            const av = a[field];
            const bv = b[field];
            const numeric = typeof av === 'number' && typeof bv === 'number';
            const cmp = numeric ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''));
            return direction === 'asc' ? cmp : -cmp;
        });
        return copy;
    }, [filteredRows, sort]);

    const residentCount = !hasClientFilters && totalCount != null ? totalCount : sortedRows.length;

    const toggleSort = (field) => {
        setSort((prev) => {
            if (!prev || prev.field !== field) return {field, direction: 'asc'};
            if (prev.direction === 'asc') return {field, direction: 'desc'};
            return null;
        });
    };

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el || isFetching || !hasMore) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceFromBottom < SCROLL_THRESHOLD_PX) {
            setOffset((prev) => prev + PAGE_SIZE);
        }
    }, [isFetching, hasMore]);

    return (
        <Box sx={{border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, backgroundColor: tokens.color.white, overflow: 'hidden'}}>
            <Stack direction="row" spacing={1} sx={{p: 2, borderBottom: `1px solid ${tokens.color.neutral200}`}}>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        border: `1px solid ${tokens.color.neutral200}`,
                        borderRadius: tokens.radius.control,
                        height: 36,
                        px: 1.5
                    }}
                >
                    <Box component="img" src={searchIcon} alt="" sx={{width: 14, height: 14}} />
                    <Box
                        component="input"
                        placeholder="Search residents..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={{border: 'none', outline: 'none', flex: 1, fontSize: '14px', fontFamily: 'inherit', backgroundColor: 'transparent'}}
                    />
                </Box>
                <Box
                    component="button"
                    type="button"
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    sx={{
                        border: `1px solid ${tokens.color.neutral200}`,
                        borderRadius: tokens.radius.control,
                        backgroundColor: filterConditions.length ? tokens.color.neutral100 : tokens.color.white,
                        height: 36,
                        px: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer'
                    }}
                >
                    <Box component="img" src={filterIcon} alt="" sx={{width: 16, height: 16}} />
                    <Typography variant="body2" fontWeight={500}>
                        Filter{filterConditions.length ? ` (${filterConditions.length})` : ''}
                    </Typography>
                </Box>
                <FilterPopover
                    anchorEl={filterAnchorEl}
                    open={Boolean(filterAnchorEl)}
                    onClose={() => setFilterAnchorEl(null)}
                    conditions={filterConditions}
                    onApply={setFilterConditions}
                />
                <Box
                    component="button"
                    type="button"
                    onClick={() => downloadResidentsCsv(COLUMNS, sortedRows)}
                    sx={{
                        border: `1px solid ${tokens.color.neutral200}`,
                        borderRadius: tokens.radius.control,
                        backgroundColor: tokens.color.white,
                        height: 36,
                        px: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer'
                    }}
                >
                    <Box component="img" src={exportIcon} alt="" sx={{width: 16, height: 16}} />
                    <Typography variant="body2" fontWeight={500}>
                        Export
                    </Typography>
                </Box>
            </Stack>

            {/* overflow: 'auto' gives the many columns a horizontal slider at the bottom of this
                box; onScroll drives infinite-scroll pagination as the user nears the bottom. */}
            <Box ref={scrollRef} onScroll={handleScroll} sx={{overflow: 'auto', maxHeight: '640px'}}>
                <Table sx={{minWidth: '100%'}} stickyHeader>
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((col) => {
                                const isActive = sort?.field === col.field;
                                return (
                                    <TableCell
                                        key={col.field}
                                        onClick={() => toggleSort(col.field)}
                                        sx={{
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                            backgroundColor: tokens.color.neutral50,
                                            borderBottom: `1px solid ${tokens.color.neutral200}`,
                                            borderRight: `1px solid ${tokens.color.neutral200}`,
                                            color: isActive ? tokens.color.neutral950 : tokens.color.neutral600,
                                            fontWeight: 500,
                                            fontSize: '14px',
                                            width: col.width,
                                            minWidth: col.width
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
                                            <span>{col.headerName}</span>
                                            <Box
                                                component="img"
                                                src={isActive ? sortActiveIcon : sortIcon}
                                                alt=""
                                                sx={{
                                                    width: 14,
                                                    height: 14,
                                                    transform: isActive && sort.direction === 'desc' ? 'rotate(180deg)' : 'none'
                                                }}
                                            />
                                        </Stack>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows.map((row, index) => (
                            <TableRow
                                key={row.id}
                                onClick={() => setSelectedIndex(index)}
                                sx={{cursor: 'pointer', '&:hover': {backgroundColor: tokens.color.neutral50}}}
                            >
                                {COLUMNS.map((col) => (
                                    <TableCell
                                        key={col.field}
                                        sx={{
                                            fontSize: '14px',
                                            whiteSpace: 'nowrap',
                                            borderRight: `1px solid ${tokens.color.neutral200}`,
                                            color: col.link ? tokens.color.sky600 : tokens.color.neutral950
                                        }}
                                    >
                                        {col.render ? col.render(row) : row[col.field] || '-'}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {sortedRows.length === 0 && !isLoading && (
                            <TableRow>
                                <TableCell colSpan={COLUMNS.length} sx={{textAlign: 'center', py: 4, color: 'text.secondary'}}>
                                    No residents found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {(isLoading || isFetching) && (
                    <Stack alignItems="center" sx={{py: 2}}>
                        <CircularProgress size={20} />
                    </Stack>
                )}
            </Box>

            <Box sx={{backgroundColor: tokens.color.neutral50, borderTop: `1px solid ${tokens.color.neutral200}`, px: 2, py: 1.5}}>
                <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral700}}>
                    {residentCount} {residentCount === 1 ? 'resident' : 'residents'}
                </Typography>
            </Box>

            <ResidentDetailDrawer
                open={selectedIndex != null}
                onClose={() => setSelectedIndex(null)}
                rows={sortedRows}
                selectedIndex={selectedIndex}
                onNavigate={setSelectedIndex}
            />
        </Box>
    );
};

export default ResidentsTable;
